import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@maxis/components';
import { SearchSection } from '@maxis/components';
import { DataGrid } from '@maxis/components';
import useFetch from '@maxis/client-logic/hooks/useFetch';
import { useBoundStore } from '@maxis/client-logic/store/useBoundStore';
import { setLocalStorageItem } from '@maxis/client-logic/utils/localStorageUtil';
import { shallow } from 'zustand/shallow';
import { findSelectedMenu } from '@maxis/client-logic/utils/menuUtil';
import { LOCAL_STORAGE_KEYS } from '@maxis/client-logic/constants/enumerates';
import { findRoutePath } from '@maxis/client-logic/utils/menuUtil';
import useMessageBox from '@maxis/client-logic/hooks/useMessageBox';

const FavoritePopup = React.forwardRef((props, ref) => {
    const { setFavoriteMenu } = useBoundStore((state) => state.menu);
    const refFavoritePopup = useRef();
    const { request } = useFetch();
    const navigate = useNavigate();
    const { alert, confirm } = useMessageBox();

    const userId = useBoundStore((state) => state.user.userId);
    const menuList = useRef([]);

    const { setSelectedMenu, setSelectedTopMenu } = useBoundStore((state) => state.menu, shallow);

    React.useImperativeHandle(ref, () => ({
        openPopup(p) {
            menuList.current = p;
            popupFunctions.showPopup();
            popupFunctions.searchData();
        },
    }));

    const confirmRemoveFavorite = async (e, obj) => {
        e.stopPropagation();
        const { MENU_NM } = obj.item;

        const result = await confirm('CONFIRM', `${MENU_NM} 메뉴를 즐겨찾기에서 삭제하시겠습니까?`);

        if (result) removeFavorite(obj);
    };

    const removeFavorite = async (obj) => {
        const param = { MENU_ID: obj.item.MENU_ID };
        const response = await request(null, {
            ...param,
            serviceId: 'FavoriteMenuService',
            process: [{ saveFavorite: '즐겨찾기 삭제' }],
            isSystem: true,
        });
        const data = response?.data?.result;

        if (data) {
            alert('SUCCESS', '삭제되었습니다.');
            const camelResponse = data.map((x) => {
                return {
                    isActive: x.IS_ACTIVE,
                    menuId: x.MENU_ID,
                    menuName: x.MENU_NM,
                    programPath: x.PGM_PATH,
                    programRoutePath: x.ROUTING_PATH,
                };
            });
            setFavoriteMenu(camelResponse);
            controls.grid.dataGrid.current?.setDataBinding(data);
        }
    };

    const controls = {
        search: {
            txtMenuNm: {
                ref: useRef(),
                label: '메뉴명',
                controlWidth: 'wpx_130',
                controlOption: {
                    onKeyUp: () => {
                        popupFunctions.searchData();
                    },
                },
            },
        },
        buttons: {
            btnSearch: {
                label: '',
                styles: 'right',
                onClick: () => {
                    popupFunctions.searchData();
                },
            },
        },
        grid: {
            dataGrid: useRef(),
            columns: [
                { header: '메뉴명', binding: 'MENU_NM', width: '*', align: 'left' },
                {
                    header: 'btn',
                    width: 30,
                    align: 'center',
                    isButton: true,
                    isButtonColor: 'colorNoBorder',
                    isButtonIcon: 'btn_circleDel',
                    clickEvent: confirmRemoveFavorite,
                },
                {
                    header: 'img',
                    width: 30,
                    align: 'center',
                    cssClass: 'cell-link',
                    visible: false,
                },
                {
                    header: '메뉴 설명',
                    binding: 'MENU_DESC',
                    align: 'left',
                    visible: false,
                },
                { header: '메뉴ID', binding: 'MENU_ID', visible: false },
                { header: 'PATH', binding: 'ROUTING_PATH', visible: false },
                { header: 'SIDE_YN', binding: 'SIDE_YN', visible: false },
                { header: 'DISPLAY_YN', binding: 'DISPLAY_YN', visible: false },
            ],
        },
        onRowClick: () => {
            const row = controls.grid.dataGrid.current?.getSelectedRow();
            if (row) {
                popupFunctions.onMenuClick(row);
            }
        },
    };

    const popupFunctions = {
        searchData: async () => {
            let value = controls.search.txtMenuNm.ref.current?.getValue() ?? '';
            const param = { USER_ID: userId, MENU_NM: value };
            const response = await request(null, {
                ...param,
                serviceId: 'FavoriteMenuService',
                process: [{ getFavoriteMenu: '즐겨찾기 메뉴 조회' }],
                isSystem: true,
            });
            const data = response?.data?.result;
            if (data) {
                controls.grid.dataGrid.current?.setDataBinding(data);
            }
        },
        showPopup: () => {
            refFavoritePopup.current?.showPopup();
        },

        closePopup: () => {
            refFavoritePopup.current?.closePopup();
        },
        settingMenu: (pathname) => {
            // 입력된 라우터 경로 기준으로 topMenu와 sideMenu를 가져옴
            const { selectedTopMenu, selectedMenu } = findSelectedMenu(menuList.current, pathname);

            if (selectedMenu) {
                // local Storage setting
                setLocalStorageItem(LOCAL_STORAGE_KEYS.MENU_ID, selectedMenu.menuId);
            }

            // 값이 존재할 경우
            if (selectedTopMenu) {
                // topMenu setting
                setSelectedTopMenu({
                    menuId: selectedTopMenu.menuId,
                    menuName: selectedTopMenu.menuName,
                    sideYn: selectedTopMenu.sideYn,
                    displayYn: selectedTopMenu.displayYn,
                    menuNameListId: selectedTopMenu.menuNameListId,
                    menuNameList: selectedTopMenu.menuNameList,
                });
            }

            if (selectedMenu) {
                // selectedMenu setting
                setSelectedMenu({
                    menuId: selectedMenu.menuId,
                    menuName: selectedMenu.menuName,
                    sideYn: selectedMenu.sideYn,
                    displayYn: selectedMenu.displayYn,
                    menuNameListId: selectedMenu.menuNameListId,
                    menuNameList: selectedMenu.menuNameList,
                });
            }
        },
        onMenuClick: (d) => {
            if (d) {
                popupFunctions.closePopup();
                // 그리드 개인화 기능을 위한 메뉴ID 인식.

                if (d.ROUTING_PATH) {
                    popupFunctions.settingMenu(d.ROUTING_PATH);
                    navigate(import.meta.env.VITE_PUBLIC_URL + d.ROUTING_PATH);
                }
            }
        },
    };

    return (
        <Modal
            ref={refFavoritePopup}
            defaultTitle={'나의 즐겨찾기'}
            closeBtnEvent={() => {
                popupFunctions.closePopup();
            }}
            showCloseBtn={true}
            defaultWidth="350px"
            defaultHeight="550px"
            showFooter={true}
            isResizable={false}
            isDraggable={false}
            backDropBg={false}
            className={'whiteHeader favoritePopup'}
        >
            <div className="searchThemeY searchIcon">
                <SearchSection
                    searchControls={controls.search}
                    numOfCols={1}
                    btnSearch={controls.buttons.btnSearch}
                />
            </div>

            <div className="listGridDiv listIcon">
                <DataGrid
                    ref={controls.grid.dataGrid}
                    columns={controls.grid.columns}
                    allowAddNew={false}
                    onRowClick={controls.onRowClick}
                />
            </div>
        </Modal>
    );
});

export default FavoritePopup;
